import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useMobile } from '../utils/useMobile';

const GIPHY_API_KEY = process.env.REACT_APP_GIPHY_API_KEY;
const GIPHY_SEARCH_URL = 'https://api.giphy.com/v1/gifs/search';
const GIPHY_TRENDING_URL = 'https://api.giphy.com/v1/gifs/trending';

const CATEGORIES = [
  { id: 'trending', label: '🔥 Trending' },
  { id: 'reactions', label: '😂 Reactions' },
  { id: 'applause', label: '👏 Clap' },
  { id: 'love', label: '❤️ Love' },
  { id: 'shocked', label: '😮 Shocked' },
  { id: 'morocco', label: '🇲🇦 Maghreb' },
  { id: 'party', label: '🎉 Party' },
];

const CURATED_FALLBACK_GIFS = [
  { id: 'c1', title: 'Goal Celebration', category: 'trending', url: 'https://media.giphy.com/media/artj92V8o75VPL7AeQ/giphy.gif' },
  { id: 'c2', title: 'Standing Ovation', category: 'applause', url: 'https://media.giphy.com/media/ely3apij36BJhoZ234/giphy.gif' },
  { id: 'c3', title: 'Mind Blown', category: 'shocked', url: 'https://media.giphy.com/media/26ufdipQqU2lhNA4g/giphy.gif' },
  { id: 'c4', title: 'This Is Fine', category: 'trending', url: 'https://media.giphy.com/media/QMHoU66sBXCAHonZsV/giphy.gif' },
  { id: 'c5', title: 'Laughing Dead', category: 'reactions', url: 'https://media.giphy.com/media/l3q2K5jinAlChoCLS/giphy.gif' },
  { id: 'c6', title: 'Heart Eyes', category: 'love', url: 'https://media.giphy.com/media/26BRv0ThflsHCqDrG/giphy.gif' },
  { id: 'c7', title: 'Vibing Cat', category: 'reactions', url: 'https://media.giphy.com/media/jpbnoe3UIa8TU8LM13/giphy.gif' },
  { id: 'c8', title: 'Morocco Flag', category: 'morocco', url: 'https://media.giphy.com/media/3o7TKSjRrfIPjeiVyM/giphy.gif' },
  { id: 'c9', title: 'Popcorn Waiting', category: 'shocked', url: 'https://media.giphy.com/media/gl0mkIZOW6Nwc/giphy.gif' },
  { id: 'c10', title: 'Dance Celebration', category: 'party', url: 'https://media.giphy.com/media/blSTtZehjAZ8I/giphy.gif' },
  { id: 'c11', title: 'Thumbs Up', category: 'reactions', url: 'https://media.giphy.com/media/111ebonMs90YLu/giphy.gif' },
  { id: 'c12', title: 'Confetti Party', category: 'party', url: 'https://media.giphy.com/media/g9582DNuQppxC/giphy.gif' },
];

const GifPicker = ({ onSelect, onClose }) => {
  const isMobile = useMobile();
  const [query, setQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('trending');
  const [gifs, setGifs] = useState([]);
  const [loading, setLoading] = useState(false);

  const fetchTrendingGifs = useCallback(async () => {
    if (!GIPHY_API_KEY) {
      setGifs(CURATED_FALLBACK_GIFS);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.get(GIPHY_TRENDING_URL, {
        params: {
          api_key: GIPHY_API_KEY,
          limit: 24,
          rating: 'g',
        },
      });
      setGifs(data.data || CURATED_FALLBACK_GIFS);
    } catch (err) {
      console.warn('Giphy trending error, using fallback:', err.message);
      setGifs(CURATED_FALLBACK_GIFS);
    } finally {
      setLoading(false);
    }
  }, []);

  const searchGifs = async (searchQuery) => {
    const q = (searchQuery !== undefined ? searchQuery : query).trim();
    if (!q) {
      fetchTrendingGifs();
      return;
    }

    setLoading(true);
    if (!GIPHY_API_KEY) {
      // Fallback local search by keyword
      const filtered = CURATED_FALLBACK_GIFS.filter(g =>
        g.title.toLowerCase().includes(q.toLowerCase()) ||
        g.category.toLowerCase().includes(q.toLowerCase())
      );
      setGifs(filtered.length > 0 ? filtered : CURATED_FALLBACK_GIFS);
      setLoading(false);
      return;
    }

    try {
      const { data } = await axios.get(GIPHY_SEARCH_URL, {
        params: {
          api_key: GIPHY_API_KEY,
          q,
          limit: 24,
          rating: 'g',
        },
      });
      setGifs(data.data || []);
    } catch (err) {
      console.warn('Giphy search error:', err);
      setGifs(CURATED_FALLBACK_GIFS);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTrendingGifs();
  }, [fetchTrendingGifs]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape') onClose();
    };
    document.addEventListener('keydown', handleEscape);
    document.body.style.overflow = 'hidden';
    return () => {
      document.removeEventListener('keydown', handleEscape);
      document.body.style.overflow = '';
    };
  }, [onClose]);

  const handleCategoryClick = (catId) => {
    setSelectedCategory(catId);
    setQuery('');
    if (catId === 'trending') {
      fetchTrendingGifs();
    } else {
      searchGifs(catId);
    }
  };

  const handleSelect = (gif) => {
    const url = gif.images?.fixed_height?.url || gif.images?.downsized_medium?.url || gif.images?.original?.url || gif.url;
    if (url) onSelect({ url, type: 'image' });
    onClose();
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    e.stopPropagation();
    searchGifs();
  };

  const content = (
    <div className="flex flex-col h-full overflow-hidden">
      {/* Search Input Bar */}
      <form
        onSubmit={handleSubmit}
        onClick={(e) => e.stopPropagation()}
        className="p-3 pb-2 flex gap-2"
      >
        <div className="relative flex-1">
          <i className="fas fa-magnifying-glass absolute left-3.5 top-1/2 -translate-y-1/2 text-zinc-400 text-xs pointer-events-none" />
          <input
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search GIFs..."
            className="w-full pl-9 pr-3.5 py-2 border border-zinc-200 dark:border-zinc-800 rounded-xl focus:outline-none focus:ring-2 focus:ring-sky-500/20 focus:border-sky-500 text-sm bg-zinc-50 dark:bg-zinc-800/60 text-zinc-900 dark:text-zinc-100"
            autoFocus
          />
        </div>
        <button
          type="submit"
          disabled={loading}
          className="px-4 py-2 bg-zinc-900 dark:bg-zinc-100 text-white dark:text-zinc-900 rounded-xl text-xs font-semibold hover:bg-zinc-800 dark:hover:bg-zinc-200 transition-colors disabled:opacity-50"
        >
          Search
        </button>
      </form>

      {/* Category Filter Chips */}
      <div className="flex items-center gap-1.5 px-3 pb-2.5 overflow-x-auto no-scrollbar flex-shrink-0">
        {CATEGORIES.map((cat) => (
          <button
            key={cat.id}
            type="button"
            onClick={() => handleCategoryClick(cat.id)}
            className={`px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all ${
              selectedCategory === cat.id && !query
                ? 'bg-sky-500 text-white shadow-xs'
                : 'bg-zinc-100 dark:bg-zinc-800/80 text-zinc-600 dark:text-zinc-400 hover:bg-zinc-200 dark:hover:bg-zinc-700'
            }`}
          >
            {cat.label}
          </button>
        ))}
      </div>

      {/* GIF Grid Gallery */}
      <div className="flex-1 overflow-y-auto px-3 pb-4 min-h-[220px]">
        {loading ? (
          <div className="flex items-center justify-center py-16">
            <div className="w-8 h-8 border-2 border-sky-500 border-t-transparent rounded-full animate-spin" />
          </div>
        ) : gifs.length === 0 ? (
          <div className="text-center py-12 text-zinc-500 dark:text-zinc-400 text-sm">
            <p>No GIFs found for "{query}".</p>
          </div>
        ) : (
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
            {gifs.map((gif, i) => {
              const imgUrl = gif.images?.fixed_height?.url || gif.images?.fixed_height_small?.url || gif.images?.original?.url || gif.url;
              return (
                <button
                  type="button"
                  key={gif.id || i}
                  onClick={() => handleSelect(gif)}
                  className="group relative block rounded-xl overflow-hidden bg-zinc-100 dark:bg-zinc-800 aspect-video hover:ring-2 hover:ring-sky-500 focus:outline-none focus:ring-2 focus:ring-sky-500 transition-all active:scale-[0.98]"
                >
                  <img
                    src={imgUrl}
                    alt={gif.title || 'GIF'}
                    loading="lazy"
                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-200"
                  />
                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/20 transition-colors pointer-events-none" />
                </button>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );

  if (isMobile) {
    return (
      <div className="fixed inset-0 z-[100] flex flex-col justify-end md:hidden" aria-modal="true" role="dialog">
        <button type="button" onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-xs z-0" aria-label="Close" />
        <div className="relative z-10 bg-white dark:bg-zinc-900 border-t border-zinc-200 dark:border-zinc-800 rounded-t-3xl shadow-2xl flex flex-col max-h-[85vh] min-h-[60vh] animate-slide-up" onClick={(e) => e.stopPropagation()}>
          <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-zinc-100 dark:border-zinc-800">
            <span className="font-bold text-sm text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
              <i className="fas fa-photo-film text-sky-500" />
              Choose a GIF
            </span>
            <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors" aria-label="Close">
              <i className="fas fa-times text-sm" />
            </button>
          </div>
          {content}
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 max-md:hidden" aria-modal="true" role="dialog">
      <button type="button" onClick={onClose} className="absolute inset-0 bg-black/60 backdrop-blur-xs z-0" aria-label="Close" />
      <div className="relative z-10 bg-white dark:bg-zinc-900 rounded-2xl shadow-2xl flex flex-col w-full max-w-lg max-h-[80vh] border border-zinc-200 dark:border-zinc-800" onClick={(e) => e.stopPropagation()}>
        <div className="flex-shrink-0 flex items-center justify-between px-4 py-3 border-b border-zinc-100 dark:border-zinc-800">
          <span className="font-bold text-sm text-zinc-900 dark:text-zinc-100 flex items-center gap-2">
            <i className="fas fa-photo-film text-sky-500" />
            Choose a GIF
          </span>
          <button type="button" onClick={onClose} className="w-8 h-8 flex items-center justify-center text-zinc-400 hover:text-zinc-600 dark:hover:text-zinc-200 hover:bg-zinc-100 dark:hover:bg-zinc-800 rounded-full transition-colors" aria-label="Close">
            <i className="fas fa-times text-sm" />
          </button>
        </div>
        {content}
      </div>
    </div>
  );
};

export default GifPicker;
